import { IfcAPI } from 'web-ifc'
import { ElMessage } from 'element-plus'
import { doc, setDoc, deleteDoc, onSnapshot } from 'firebase/firestore'
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'
import { db } from '~/src/scripts/log-envent'
import { MTLLoader } from '~/src/models/utils/MTLLoader'
import { OBJLoader } from '~/src/models/utils/OBJLoader'
import { GLTFLoader } from '~/src/models/utils/GLTFLoader'
import { FBXLoader } from '~/src/models/utils/FBXLoader'
import { STLLoader } from '~/src/models/utils/STLLoader'
import { Rhino3dmLoader } from '~/src/models/utils/3DMLoader'
import { request, isLocal, sleep } from '~/src/scripts/utils'
import i18n from '~/src/lang'
import JSZip from 'jszip'
import axios from 'axios'
import { useUploadStore } from './useUploadStore'

export const FILE_TYPES = [
  'obj',
  'gltf',
  'glb',
  'fbx',
  'stl',
  'las',
  'xyz',
  'ply',
  'pcd',
  'e57',
  'ifc',
  'skp',
  'blend',
  'stp',
  'step',
  '3dm'
]

export const SUPPORT_TYPES = [
  'jpg',
  'png',
  'jpeg',
  'gif',
  'bmp',
  'webp',
  'tga',
  'tiff',
  'tif',
  'dds',
  'hdr',
  'exr',
  'bin',
  'sldprt',
  'sldasm',
  'xml',
  'prj',
  'csv',
  'json'
]

export const ANALYZE_PERCENT = 0.5
export const ZIP_PERCENT = 0.1

const t = i18n.global.t

class UploadFile {
  unsubscriber = null

  queue = null

  file = null

  modelFile = null

  mtlFile = null

  serverFile = null

  supportFiles = []

  jpgMap = {}

  folderId = 0

  space = null

  name = ''

  type = ''

  isZip = false

  isOriginal = false

  fileKey = ''

  uploadKey = ''

  transformId = ''

  _status = 'waiting'

  get id() {
    return this.fileKey
  }

  get isCloud() {
    return !this.isOriginal && ['las', 'xyz', 'ply', 'pcd', 'e57'].includes(this.type)
  }

  get key() {
    const type = this.type === 'step' ? 'stp' : this.type
    if (this.isCloud) {
      return type ? `upload_cloud_${type}` : 'upload_cloud'
    }
    if (this.serverType) {
      return `upload_${this.serverType}_${type}`
    }
    return `upload_${type}`
  }

  get mineType() {
    if (this.type === 'skp') {
      return 'application/x-koan'
    }
    return ''
  }

  get serverType() {
    if (['skp', 'blend', 'stp', 'step'].includes(this.type)) {
      return 'glb'
    }
    return ''
  }

  get status() {
    return this._status
  }

  set status(status = '') {
    const uploadStore = useUploadStore()
    this._status = status
    uploadStore.updateUploadingFile(this, {
      status: this._status
    })
  }

  constructor(queue, props = {}) {
    this.queue = queue
    this.file = props.file
    this.modelFile = props.modelFile
    this.mtlFile = props.mtlFile
    this.supportFiles = props.supportFiles || []
    this.folderId = props.folderId
    this.space = props.space
    this.name = props.name
    this.type = props.type
    this.isZip = !!props.isZip
    this.fileKey = props.fileKey
  }

  async getSize(file, zip) {
    let size = 0
    if (this.isCloud) {
      if (!zip) {
        zip = await JSZip.loadAsync(file)
      }
      zip.forEach((_, f) => {
        if (!f.dir) {
          const s = f._data?.uncompressedSize || 0
          size += s
        }
      })
      return size
    }
    if (this.serverFile) {
      size += this.serverFile.size
    } else if (this.modelFile) {
      size += this.modelFile.size
    }
    for (let i = 0; i < this.supportFiles.length; i++) {
      const f = this.supportFiles[i]
      if (f.file) {
        size += f.file.size
      }
    }
    return size
  }

  handleProgress(progress = {}, start = 0, ratio = 1) {
    const uploadStore = useUploadStore()
    const { loaded = 0, total = 1 } = progress
    uploadStore.updateUploadingFile(this, {
      percent: start + (loaded / total) * ratio
    })
  }

  handleError(msg = '') {
    if (this.checkActive()) {
      if (!navigator.onLine) {
        ElMessage({ message: t('file.import.error.checkNet'), type: 'error' })
      } else {
        const controller = new AbortController()
        const timer = setTimeout(() => controller.abort(), 3000)
        fetch('https://scanat.app', { mode: 'no-cors', signal: controller.signal })
          .then(() => {
            if (msg) {
              ElMessage({ message: msg, type: 'error' })
            }
          })
          .catch(() => {
            ElMessage({ message: t('file.import.error.checkNet'), type: 'error' })
          })
          .finally(() => clearTimeout(timer))
      }
    }
    this.status = 'error'
    this.queue?.queueOut(this)
    this.clearImages()
  }

  handleSuccess() {
    this.status = 'success'
    this.queue?.queueOut(this)
    this.clearImages()
  }

  handleSubscribe(transformId = '') {
    const uploadStore = useUploadStore()
    uploadStore.updateUploadingFile(this, {
      transformId
    })
    this.transformId = transformId
  }

  handleUnsubscribe() {
    const uploadStore = useUploadStore()
    uploadStore.updateUploadingFile(this, {
      transformId: ''
    })
    this.transformId = ''
    this.unsubscriber?.()
    this.unsubscriber = null
  }

  createImages() {
    this.clearImages()
    for (let i = 0; i < this.supportFiles.length; i++) {
      const { name, file } = this.supportFiles[i]
      this.jpgMap[name] = URL.createObjectURL(file)
    }
  }

  clearImages() {
    const jpgKeys = Object.keys(this.jpgMap)
    if (jpgKeys.length > 0) {
      jpgKeys.forEach(key => URL.revokeObjectURL(this.jpgMap[key]))
      this.jpgMap = {}
    }
  }

  checkActive() {
    const active = this.queue?.uploadingFiles?.includes(this)
    if (!active && this.unsubscriber) {
      this.handleUnsubscribe()
    }
    return active
  }

  async checkMTL() {
    if (this.mtlFile && this.checkActive()) {
      const mtlUrl = URL.createObjectURL(this.mtlFile)
      return new Promise(resolve => {
        new MTLLoader().load(
          mtlUrl,
          async materials => {
            try {
              await materials?.preload?.()
              URL.revokeObjectURL(mtlUrl)
              resolve(materials)
            } catch {
              URL.revokeObjectURL(mtlUrl)
              resolve(false)
            }
          },
          undefined,
          () => {
            URL.revokeObjectURL(mtlUrl)
            resolve(false)
          },
          this.jpgMap
        )
      })
    }
    return false
  }

  start() {
    this.analyze()
  }

  async analyze() {
    this.status = 'analyzing'
    this.handleProgress()
    if (this.modelFile && this.checkActive()) {
      if (this.isCloud) {
        this.queue?.appendConvert(this)
      } else {
        let loader, url
        this.createImages()
        switch (this.type) {
          case 'obj':
            if (this.mtlFile) {
              const materials = await this.checkMTL()
              if (materials) {
                loader = new OBJLoader().setMaterials(materials)
              }
            } else {
              loader = new OBJLoader()
            }
            break
          case 'gltf':
          case 'glb':
            loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder)
            break
          case 'fbx':
            loader = new FBXLoader()
            break
          case 'stl':
            loader = new STLLoader()
            break
          case '3dm':
            loader = new Rhino3dmLoader()
            loader.setLibraryPath(
              isLocal ? 'https://localhost:5173/webModel/jsm/rhino3dm/' : '/webModel/jsm/rhino3dm/'
            )
            break
        }
        if (this.checkActive()) {
          if (loader) {
            try {
              url = URL.createObjectURL(this.modelFile)
              loader.load(
                url,
                o => {
                  if (url) URL.revokeObjectURL(url)
                  if (
                    o?.children?.length ||
                    o?.scene?.children?.length ||
                    o?.attributes?.position?.count
                  ) {
                    this.upload()
                    this.clearImages()
                  } else {
                    this.handleError(t('file.import.error.parsingFailed'))
                  }
                },
                p => {
                  this.handleProgress(p, 0, ANALYZE_PERCENT)
                },
                () => {
                  if (url) URL.revokeObjectURL(url)
                  this.handleError(t('file.import.error.parsingFailed'))
                },
                this.jpgMap
              )
            } catch {
              if (url) URL.revokeObjectURL(url)
              this.handleError(t('file.import.error.parsingFailed'))
            } finally {
              loader = null
            }
          } else if (this.type === 'ifc') {
            try {
              loader = new IfcAPI()
              await loader.Init((path, prefix) => {
                if (path?.includes('web-ifc.wasm')) {
                  return new URL('~/src/scripts/convert/web-ifc.wasm', import.meta.url).toString()
                }
                return `${prefix}${path}`
              })
              this.handleProgress({ loaded: 0.5, total: 1 }, 0, ANALYZE_PERCENT)
              await sleep(100)
              const modelData = await this.modelFile.arrayBuffer()
              this.handleProgress({ loaded: 1, total: 1 }, 0, ANALYZE_PERCENT)
              const modelId = loader.OpenModel(new Uint8Array(modelData))
              if (modelId === -1) {
                this.handleError(t('file.import.error.parsingFailed'))
              } else {
                this.upload()
                this.clearImages()
              }
              loader.Dispose()
            } catch (err) {
              this.handleError(t('file.import.error.parsingFailed'))
            } finally {
              loader = null
            }
          } else if (this.serverType) {
            await this.loadFromServer()
            if (this.serverFile) {
              this.upload()
              this.clearImages()
            } else {
              this.handleError()
            }
          } else {
            this.handleError(t('file.import.error.parsingFailed'))
          }
        }
      }
    } else {
      this.handleError(t('file.import.error.parsingFailed'))
    }
  }

  async transform(key, callback) {
    try {
      const convertRes = await request(`webModels/convert?key=${key}`, {}, true)
      const transformId = convertRes?.data?.id
      if (convertRes?.code === 200 && transformId !== undefined) {
        this.handleSubscribe(transformId)
        return new Promise(async resolve => {
          const userInfo = JSON.parse(sessionStorage.getItem('SCANAT_USER_INFO')) ?? {}
          const userId = userInfo?.id
          if (userId) {
            try {
              const taskDoc = doc(db, 'modelConvert', `${userId}`, 'tasks', `${transformId}`)
              await setDoc(taskDoc, { visitor: userId }, { merge: true })
              this.unsubscriber?.()
              this.unsubscriber = onSnapshot(taskDoc, async doc => {
                const { status = '', url = '' } = doc.data() || {}
                switch (status) {
                  case 'success':
                    deleteDoc(taskDoc)
                    if (url) {
                      const res = await fetch(url)
                      const contentLength = res?.headers?.get('content-length')
                      if (contentLength && res.status === 200 && res.ok) {
                        resolve({ status: 'success', contentLength, res })
                      } else {
                        resolve({ status: 'error' })
                      }
                    } else {
                      resolve({ status: 'error' })
                    }
                    break
                  case 'fail':
                    deleteDoc(taskDoc)
                    resolve({ status: 'error' })
                    break
                  case 'processing':
                    callback?.({ loaded: 0.2, total: 1 })
                    break
                }
              })
            } catch (err) {
              console.error(err)
              resolve({ status: 'error' })
            }
          } else {
            console.error('user not found')
            resolve({ status: 'error' })
          }
        })
      } else {
        return { status: 'error' }
      }
    } catch (err) {
      console.error(err)
      return { status: 'error' }
    }
  }

  async download(key = '', callback) {
    try {
      if (!key || !this.checkActive()) {
        return null
      }
      const { status, contentLength, res: downloadRes } = await this.transform(key, callback)
      if (status === 'success') {
        const total = parseInt(contentLength, 10)
        let loaded = 0
        const res = new Response(
          new ReadableStream({
            start(controller) {
              const reader = downloadRes.body?.getReader()
              async function push() {
                const { done, value } = await reader?.read()
                if (done) {
                  controller.close()
                  return
                }
                loaded += value.byteLength
                controller.enqueue(value)
                callback?.({ loaded: (total + loaded) / 2, total })
                push()
              }
              push()
            }
          })
        )
        const blob = await res.blob()
        blob.name = `${this.name}.${this.serverType || this.type}`
        return blob
      }
      return null
    } catch {
      return null
    } finally {
      this.handleUnsubscribe()
    }
  }

  async loadFromServer(file, fileType) {
    try {
      const zipPercent = ANALYZE_PERCENT / 4
      const uploadPercent = ANALYZE_PERCENT / 2
      this.serverFile = null
      if (!this.checkActive()) return
      if (!file) {
        file = this.modelFile
      }
      if (!fileType) {
        fileType = this.type === 'step' ? 'stp' : this.type
      }
      const transformRes = await request(
        'webModels/uploadTransform',
        {
          fileType,
          timestamp: Date.now()
        },
        true,
        'GET'
      )
      if (transformRes?.code === 200 && transformRes?.data) {
        const uploadRes = await axios({
          url: transformRes.data.url,
          method: 'PUT',
          data: file,
          headers: {
            'Content-type': 'application/octet-stream'
          },
          onUploadProgress: p => {
            this.handleProgress(p, zipPercent, uploadPercent)
          }
        })
        if (uploadRes?.status === 200) {
          const ratio = ANALYZE_PERCENT - zipPercent - uploadPercent
          this.serverFile = await this.download(transformRes.data.key, ({ total, loaded }) => {
            this.handleProgress({ loaded, total }, zipPercent + uploadPercent, ratio)
          })
          if (!this.serverFile) {
            this.handleError(t('file.import.error.system'))
            return
          }
        }
      }
    } catch {
      this.handleError(t('file.import.error.system'))
    }
  }

  async originalUpload() {
    this.isOriginal = true
    this.upload()
  }

  async upload(file) {
    try {
      if (!this.checkActive()) return
      this.status = 'uploading'
      let isServer = false
      if (!file) {
        if (this.serverFile) {
          file = this.serverFile
          isServer = true
        } else {
          file = this.file
        }
      }
      let zip
      if (this.isCloud) {
        if (this.supportFiles.length > 0) {
          zip = await JSZip.loadAsync(file)
        }
      } else if (!this.isZip || isServer) {
        zip = new JSZip()
        const folder = zip.folder(this.key)
        folder.file(file.name, file)
      }
      if (zip) {
        this.supportFiles.forEach(item => {
          zip.file(`${this.key}/${item.name}`, item.file)
        })
        file = await zip.generateAsync(
          {
            type: 'blob',
            compression: 'DEFLATE',
            compressionOptions: {
              level: 3
            }
          },
          info => {
            this.handleProgress(
              { loaded: info?.percent || 0, total: 100 },
              ANALYZE_PERCENT,
              ZIP_PERCENT
            )
          }
        )
      }
      if (!this.uploadKey) {
        const res = await request('webModels/requestFileKey', {}, true, 'GET')
        if (res.code === 200 && res.data) {
          this.uploadKey = res.data
        }
      }
      if (this.uploadKey) {
        const res = await request(
          'webModels/uploadModel',
          {
            fileKey: this.uploadKey,
            timestamp: Date.now()
          },
          true,
          'GET'
        )
        if (res.code === 200 && res.data?.url) {
          const uploadRes = await axios({
            url: res.data.url,
            method: 'PUT',
            data: file,
            headers: {
              'Content-Type': 'application/zip'
            },
            onUploadProgress: p => {
              this.handleProgress(
                p,
                ANALYZE_PERCENT + ZIP_PERCENT,
                1 - ANALYZE_PERCENT - ZIP_PERCENT
              )
            }
          })
          if (uploadRes?.status === 200) {
            const size = await this.getSize(file, zip)
            await this.addModel(size)
            return
          }
        }
      }
      this.handleError(t('file.import.error.system'))
    } catch (err) {
      console.error(err)
      this.handleError(t('file.import.error.system'))
    }
  }

  async addModel(size) {
    try {
      const res = await request(
        `webModels/spaces/${this.space}/importModel`,
        {
          fileKey: this.uploadKey,
          fileName: this.name,
          fileSize: size,
          modelType: this.isCloud ? 'cloud' : this.serverType || this.type,
          originType: this.isZip ? 'zip' : this.type,
          parentId: this.folderId == 0 ? null : this.folderId
        },
        true,
        'PUT'
      )
      if (res?.code === 200) {
        this.handleSuccess()
      } else {
        this.handleError(t('file.import.error.system'))
      }
    } catch {
      this.handleError(t('file.import.error.system'))
    }
  }
}

class UploadQueue {
  worker = null

  MAX_UPLOADING_COUNT = 3

  queueFiles = []

  uploadingFiles = []

  canConvert = false

  convertFiles = []

  convertingFile = null

  init() {
    this.worker = new Worker(new URL('~/src/scripts/convert/uploadWorker.js', import.meta.url), {
      type: 'module'
    })
    this.worker.postMessage({
      type: 'init'
    })
    this.worker.onmessage = this.handleConvert.bind(this)
  }

  clear() {
    this.queueFiles = []
    this.uploadingFiles = []
    this.canConvert = false
    this.convertFiles = []
    this.convertingFile = null
    if (this.worker) {
      this.worker.terminate()
      this.worker = null
    }
  }

  appendQueue(files = [], lazyStart = false) {
    this.queueFiles.push(...files.map(f => (f instanceof UploadFile ? f : new UploadFile(this, f))))
    if (lazyStart) {
      setTimeout(() => this.startQueue(), 500)
    } else {
      this.startQueue()
    }
  }

  queueOut(file) {
    this.queueFiles = this.queueFiles.filter(f => f !== file)
    this.uploadingFiles = this.uploadingFiles.filter(f => f !== file)
    this.startQueue()
  }

  startQueue() {
    const count = this.MAX_UPLOADING_COUNT - this.uploadingFiles.length
    const files = this.queueFiles.splice(0, count)
    this.uploadingFiles.push(...files)
    files.forEach(f => f.start())
  }

  appendConvert(file) {
    if (this.convertFiles.includes(file)) return
    this.convertFiles.push(file)
    this.startConvert()
  }

  endConvert() {
    if (this.convertingFile) {
      this.convertFiles = this.convertFiles.filter(f => f !== this.convertingFile)
      this.convertingFile = null
      this.startConvert()
    }
  }

  startConvert() {
    if (!this.convertingFile && this.canConvert) {
      this.convertingFile = this.convertFiles.shift()
      if (this.convertingFile) {
        this.worker.postMessage({
          type: 'key',
          key: this.convertingFile.key,
          fileType: this.convertingFile.type?.toLowerCase()
        })
        const reader = new FileReader()
        reader.onload = e => {
          const data = new Uint8Array(e.target.result)
          this.worker.postMessage(data.buffer, [data.buffer])
        }
        reader.onerror = () => {
          this.convertingFile?.handleError?.(t('file.import.error.parsingFailed'))
          this.endConvert()
        }
        reader.readAsArrayBuffer(this.convertingFile.modelFile)
      }
    }
  }

  handleConvert(event) {
    const data = event.data
    if (data instanceof ArrayBuffer) {
      this.convertingFile?.upload?.(new Blob([data], { type: 'application/zip' }))
      this.endConvert()
    } else {
      const { type } = data || {}
      switch (type) {
        case 'progress':
          const p = data?.progress <= 1 ? data.progress : 1
          if (data?.progressType === 'zip') {
            this.convertingFile?.handleProgress?.(
              { total: 1, loaded: p },
              ANALYZE_PERCENT,
              ZIP_PERCENT
            )
          } else {
            this.convertingFile?.handleProgress?.({ total: 1, loaded: p }, 0, ANALYZE_PERCENT)
          }
          break
        case 'no-need-convert':
          this.convertingFile?.originalUpload?.()
          this.endConvert()
          break
        case 'error':
          this.convertingFile?.handleError?.(t('file.import.error.parsingFailed'))
          this.endConvert()
          break
        case 'initialized':
          this.canConvert = true
          this.startConvert()
          break
      }
    }
  }
}

export default new UploadQueue()
